import {Button, type Column, Table, TableRow} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat} from 'platform/locale';

import type {ReactNode} from 'react';

import type {InterestConfigurationListItemResponseBody} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import {MoneyText} from '../MoneyText/MoneyText';
import {NewVehicleStatusFlag} from '../NewVehicleStatusFlag/NewVehicleStatusFlag';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

type Configuration = InterestConfigurationListItemResponseBody;

export type ConfigurationsTableProps = {
  configurations: Configuration[];
  onReview: (configurationId: string) => void;
  renderAnnotation?: (configuration: Configuration) => ReactNode;
  renderAction?: (configuration: Configuration) => ReactNode;
};

const JUSTIFY = {left: 'flex-start', right: 'flex-end'} as const;

type CellAlign = keyof typeof JUSTIFY;

/**
 * A bare `th` centres its label and carries no padding, so raw column labels never line up with
 * the cells below them. Every header here shares Cell's padding and alignment instead.
 */
const column = (label: string, options?: {width?: Column['width']; align?: CellAlign}): Column => ({
  width: options?.width,
  element: (
    <Box paddingHorizontal={3} paddingVertical={2}>
      <HStack justify={JUSTIFY[options?.align ?? 'left']}>
        <Text size="xSmall" color="secondary" alternative noWrap>
          {label}
        </Text>
      </HStack>
    </Box>
  ),
});

function Cell(props: {children: ReactNode; align?: CellAlign}) {
  return (
    <Box paddingHorizontal={3} paddingVertical={3}>
      <HStack align="center" justify={JUSTIFY[props.align ?? 'left']}>
        {props.children}
      </HStack>
    </Box>
  );
}

export function ConfigurationsTable(props: ConfigurationsTableProps) {
  return (
    <Table
      // Without fixed widths the trailing action column eats the free space and the data columns
      // drift towards the middle of a wide card.
      columns={[
        column(t('entity.newVehicle.labels.configuration', 'Configuration')),
        column(t('entity.newVehicle.labels.status', 'Status'), {width: '18%'}),
        column(t('entity.newVehicle.labels.priceGross', 'Price (gross)'), {
          width: '18%',
          align: 'right',
        }),
        column(t('general.labels.created', 'Created'), {width: '14%', align: 'right'}),
        // Hugs the right edge so the data columns keep the width.
        ...(props.renderAction ? [{width: '1%', element: ''} satisfies Column] : []),
      ]}
    >
      {props.configurations.map((configuration) => {
        const annotation = props.renderAnnotation?.(configuration);
        const action = props.renderAction?.(configuration);
        return (
          <TableRow key={configuration.id} onRowClick={() => props.onReview(configuration.id)}>
            <Box paddingHorizontal={3} paddingVertical={3}>
              <VStack spacing={1}>
                {/* Row in a column stack: keeps the link from stretching and centring its label. */}
                <HStack spacing={2} align="center" wrap>
                  <Button
                    type="button"
                    variant="link"
                    size="small"
                    title={configuration.label}
                    onClick={(event) => {
                      event.stopPropagation();
                      props.onReview(configuration.id);
                    }}
                  />
                  <Show when={annotation}>{annotation}</Show>
                </HStack>
                <Text size="xSmall" color="secondary">
                  {`${configuration.modelYear} · ${configuration.trim} · ${configuration.variant}`}
                </Text>
              </VStack>
            </Box>
            <Cell>
              <NewVehicleStatusFlag
                group={configuration.orderStatus ? 'orderStatus' : 'priceValidity'}
                value={configuration.orderStatus ?? configuration.priceValidity}
                size="small"
              />
            </Cell>
            <Cell align="right">
              <MoneyText
                amount={configuration.totalGross}
                currency={configuration.currency}
                alternative
                align="right"
                noWrap
              />
            </Cell>
            <Cell align="right">
              <Text size="small" color="secondary" noWrap>
                <DateTimeFormat value={parseDate(configuration.createdAt)} format="dateMedium" />
              </Text>
            </Cell>
            {/* Ternary, not Show: TableRow maps each child to a `td`, so a rendered-empty Show
                would add a cell the header has no column for. */}
            {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
            {props.renderAction ? <Cell align="right">{action}</Cell> : null}
          </TableRow>
        );
      })}
    </Table>
  );
}
